"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { Header } from "./Header";
import { SearchFilters } from "./SearchFilters";
import { JobCard } from "./JobCard";
import { TrustedOrganizations } from "./TrustedOrganizations";
import { Footer } from "./Footer";
import FinancialAid from "./FinancialAid";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { DemoTour } from "./components/DemoTour";
import { useSOCApi } from "./hooks/useSOCApi";
import { socApiService } from "./api/socApiService";
// import { onetComprehensiveApiService } from "./api/onetComprehensiveApiService"; // Temporarily disabled - using CareerOneStop API only
import { industryApiService } from "./api/industryApiService";
import JobDescription from "./components/JobDescription";
import JobPostingsModal from "./components/JobPostingsModal";
import { About } from "./About";
import { Contact } from "./Contact";
import { formatUrl } from "./utils/urlUtils";
import { exportIndustriesToExcel, exportFinancialAidToExcel, exportBusinessesToExcel } from "./utils/excelExport";
import { QuickStartGuide } from "./QuickStartGuide";

import { Job, SOCOccupation, Industry, SortOption } from "./types";

// Feature Flags
const FEATURE_FLAGS = {
  SHOW_TRUSTED_ORGANIZATIONS: false, // Set to false to hide the TrustedOrganizations section
};
const MIN_CURRENT_JOBS_FOR_GROWTH = 3;

function CareerPathwaysPortal() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSector, setSelectedSector] = useState("all");
  const [sortBy, setSortBy] = useState<SortOption>("default");
  const [sectors, setSectors] = useState<string[]>(["all"]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFinancialAidVisible, setIsFinancialAidVisible] = useState(false);
  const [isAboutModalVisible, setIsAboutModalVisible] = useState(false);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isJobDetailModalVisible, setIsJobDetailModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isJobPostingsModalVisible, setIsJobPostingsModalVisible] = useState(false);
  const [isQuickStartVisible, setIsQuickStartVisible] = useState(false);
  const [jobPostingsSOCCode, setJobPostingsSOCCode] = useState("");
  const [jobPostingsJobTitle, setJobPostingsJobTitle] = useState("");
  const [isIndustriesModalVisible, setIsIndustriesModalVisible] = useState(false);
  const [selectedSOCCode, setSelectedSOCCode] = useState("");
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [industries, setIndustries] = useState<any[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industriesError, setIndustriesError] = useState("");
  
  // All Industries Modal State
  const [isAllIndustriesModalVisible, setIsAllIndustriesModalVisible] = useState(false);
  const [allIndustries, setAllIndustries] = useState<any[]>([]);
  const [allIndustriesLoading, setAllIndustriesLoading] = useState(false);
  const [allIndustriesError, setAllIndustriesError] = useState("");
  const [industriesPagination, setIndustriesPagination] = useState({
    limit: 50,
    skip: 0,
    hasMore: false,
    totalPages: 0,
    currentPage: 1,
    totalCount: 0
  });

  const [businesses, setBusinesses] = useState<any[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [businessesError, setBusinessesError] = useState("");
  const [isBusinessesModalVisible, setIsBusinessesModalVisible] = useState(false);
  
  const [selectedCareerCluster, setSelectedCareerCluster] = useState("all");
  const [careerClusters, setCareerClusters] = useState<string[]>(["all"]);
  
  // Detailed Job Data State
  const [onetData, setOnetData] = useState<SOCOccupation | null>(null);
  const [onetLoading, setOnetLoading] = useState(false);
  const [activeOnetTab, setActiveOnetTab] = useState<'skills' | 'knowledge' | 'technology' | 'tools'>('skills');
  const [isDemoTourActive, setIsDemoTourActive] = useState(false);
  const jobsPerPage = 50;
  const [includeSmallGrowthOccupations, setIncludeSmallGrowthOccupations] = useState(false);

  // Use the custom hook for API integration
  const {
    jobs,
    loading,
    error,
    totalJobs,
    loadJobs,
    clearError,
  } = useSOCApi();

  const isGrowthTrendSort = sortBy === 'growth-trend-desc' || sortBy === 'growth-trend-asc';
  const canAdjustMinCurrentJobs = selectedCareerCluster !== 'all';
  const minCurrentJobsFilter = isGrowthTrendSort
    ? (includeSmallGrowthOccupations ? 0 : MIN_CURRENT_JOBS_FOR_GROWTH)
    : undefined;

  useEffect(() => {
    if (!canAdjustMinCurrentJobs && includeSmallGrowthOccupations) {
      setIncludeSmallGrowthOccupations(false);
    }
  }, [canAdjustMinCurrentJobs, includeSmallGrowthOccupations]);

  // Load sectors and career clusters from database on component mount
  useEffect(() => {
    const loadSectorsAndClusters = async () => {
      try {
        const [frontendSectors, frontendCareerClusters] = await Promise.all([
          socApiService.getFrontendSectors(),
          socApiService.getFrontendCareerClusters()
        ]);
        setSectors(frontendSectors);
        setCareerClusters(frontendCareerClusters);
      } catch (err) {
        console.error('Failed to load sectors and career clusters from database:', err);
        // Fallback to known sectors and clusters
        setSectors(['all', 'Aviation', 'Education', 'Healthcare', 'Other']);
        setCareerClusters(['all', 'Marketing Sales / Management', 'Information Technology', 'Manufacturing', 'Architecture & Construction', 'Education & Training', 'Civics, Public Safety', 'Hospitality & Tourism', 'Finance', 'Marketing Sales', 'Supply Chain & Transportation', 'Other']);
      }
    };
    loadSectorsAndClusters();
  }, []);

  // Reset currentPage to 1 when searchTerm, selectedSector, or selectedCareerCluster changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSector, selectedCareerCluster]);

  // Debounced search effect - wait 2 seconds after user stops typing before calling API
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        setIsSearching(true);
      }
      const skip = (currentPage - 1) * jobsPerPage;
      loadJobs({
        limit: jobsPerPage,
        skip,
        sector: selectedSector !== 'all' ? selectedSector : undefined,
        career_cluster: selectedCareerCluster !== 'all' ? selectedCareerCluster : undefined,
        search: searchTerm || undefined,
        sort_by: sortBy !== 'default' ? sortBy : undefined,
        min_current_jobs: minCurrentJobsFilter,
      }).finally(() => {
        setIsSearching(false);
      });
    }, searchTerm ? 2000 : 0); // 2 second (2000ms) debounce for search - wait for user to stop typing, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [currentPage, searchTerm, selectedSector, selectedCareerCluster, sortBy, jobsPerPage, loadJobs, minCurrentJobsFilter]);

  // Handle search term changes with debouncing
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Handle sector changes
  const handleSectorChange = (sector: string) => {
    setSelectedSector(sector);
    setCurrentPage(1);
  };

  // Handle career cluster changes
  const handleCareerClusterChange = (careerCluster: string) => {
    setSelectedCareerCluster(careerCluster);
    setCurrentPage(1);
  };

  // Handle sort changes
  const handleSortChange = (sortOption: SortOption) => {
    setSortBy(sortOption);
    setCurrentPage(1);
  };

  const handleIncludeSmallGrowthOccupationsChange = (include: boolean) => {
    if (include && !canAdjustMinCurrentJobs) {
      return;
    }
    setIncludeSmallGrowthOccupations(include);
  };

  // Demo tour handlers
  const handleStartDemoTour = () => {
    setIsDemoTourActive(true);
  };

  const handleCloseDemoTour = () => {
    setIsDemoTourActive(false);
  };

  // Listen for auto-start tour event
  useEffect(() => {
    const handleAutoStartTour = () => {
      setIsDemoTourActive(true);
    };

    window.addEventListener('startDemoTour', handleAutoStartTour);
    return () => {
      window.removeEventListener('startDemoTour', handleAutoStartTour);
    };
  }, []);

  // Get filtered jobs - all filtering is now handled server-side
  const getFilteredJobs = () => {
    // All filtering (sector, career_cluster, search) and sorting is now handled server-side
    // in the loadJobs function, so we just return the jobs as they come from the server
    return jobs;
  };

  // Handle job card click
  const handleJobCardClick = (job: Job) => {
    setSelectedJob(job);
    setIsJobDetailModalVisible(true);
    
    // Dispatch event to signal job modal opened
    window.dispatchEvent(new CustomEvent('jobModalOpened', { detail: { job } }));
    
    // Skip O*NET data loading for now - we'll use CareerOneStop API only
    // Check if demo tour is active - don't load ONET data during tour
    const tourElement = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
    if (!tourElement) {
      // Skip loading O*NET data - we'll use CareerOneStop API for job postings
      // loadOnetData(job.socCode);
      setOnetData(null); // Set to null to use fallback data in JobDescription
    }
  };

  // Load detailed job data - Temporarily disabled, using CareerOneStop API only
  // const loadOnetData = async (socCode: string) => {
  //   setOnetLoading(true);
  //   
  //   try {
  //     const data = await onetComprehensiveApiService.getOccupationDetails(socCode);
  //     setOnetData(data);
  //   } catch (error) {
  //     console.error('Failed to load detailed job data:', error);
  //     // Don't show error to user, just use fallback data
  //     setOnetData(null); // This will trigger fallback data in JobDescription
  //   } finally {
  //     setOnetLoading(false);
  //   }
  // };



  // Handle industries button click
  const handleIndustriesClick = async (socCode: string, jobTitle: string) => {
    setSelectedSOCCode(socCode);
    setSelectedJobTitle(jobTitle);
    setIsIndustriesModalVisible(true);
    setIndustriesLoading(true);
    setIndustriesError("");

    // Reset pagination
    setIndustriesPagination({
      limit: 50,
      skip: 0,
      hasMore: false,
      totalPages: 0,
      currentPage: 1,
      totalCount: 0
    });

    // Dispatch event to signal industries modal opened
    window.dispatchEvent(new CustomEvent('industriesModalOpened', { detail: { socCode, jobTitle } }));

    try {
      // Use the new dedicated industry API service
      const response = await industryApiService.getIndustriesBySOC(socCode, {
        limit: 50,
        skip: 0,
        sort_by: "Employment" // Sort by employment by default
      });
      
      if (response.success) {
        setIndustries(response.industries || []);
        if (response.pagination) {
          setIndustriesPagination({
            limit: response.pagination.limit,
            skip: response.pagination.skip,
            hasMore: response.pagination.has_more,
            totalPages: response.pagination.total_pages,
            currentPage: response.pagination.current_page,
            totalCount: response.total_count || 0
          });
        }
      } else {
        setIndustriesError(response.message || "No industries found for this SOC code");
        setIndustries([]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load industries. Please try again.";
      setIndustriesError(errorMessage);
      setIndustries([]);
    } finally {
      setIndustriesLoading(false);
    }
  };

  // Handle all industries button click
  const handleAllIndustriesClick = async (socCode: string, jobTitle: string) => {
    setSelectedSOCCode(socCode);
    setSelectedJobTitle(jobTitle);
    setIsAllIndustriesModalVisible(true);
    setAllIndustriesLoading(true);
    setAllIndustriesError("");

    try {
      // Use the new dedicated industry API service
      const response = await industryApiService.getIndustriesBySOC(socCode, {
        limit: 500, // Get all industries (max allowed)
        skip: 0,
        sort_by: "Employment" // Sort by employment by default
      });
      if (response.success) {
        setAllIndustries(response.industries);
      } else {
        setAllIndustriesError(response.message || "No industries found for this SOC code");
        setAllIndustries([]);
      }
    } catch (error) {
      setAllIndustriesError("Failed to load industries. Please try again.");
      setAllIndustries([]);
    } finally {
      setAllIndustriesLoading(false);
    }
  };

  // Handle businesses button click (uses enhanced backend payload)
  const handleBusinessesClick = async (socCode: string, jobTitle: string) => {
    setSelectedSOCCode(socCode);
    setSelectedJobTitle(jobTitle);
    setIsBusinessesModalVisible(true);
    setBusinessesLoading(true);
    setBusinessesError("");

    try {
      const resp = await socApiService.getBusinessesBySOC(socCode, 100);
      
      setBusinesses(resp.businesses || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load businesses.';
      setBusinessesError(errorMessage);
      setBusinesses([]);
    } finally {
      setBusinessesLoading(false);
    }
  };

  // Handle close job detail modal
  const handleCloseJobDetailModal = () => {
    setIsJobDetailModalVisible(false);
    setSelectedJob(null);
    setOnetData(null);
    setActiveOnetTab('skills');
  };

  // Load more industries for pagination
  const loadMoreIndustries = async () => {
    if (!selectedSOCCode || industriesLoading || !industriesPagination.hasMore) return;
    
    setIndustriesLoading(true);
    const nextSkip = industriesPagination.skip + industriesPagination.limit;
    
    try {
      // Use the new dedicated industry API service
      const response = await industryApiService.getIndustriesBySOC(selectedSOCCode, {
        limit: industriesPagination.limit,
        skip: nextSkip,
        sort_by: "Employment"
      });
      if (response.success) {
        setIndustries(prev => [...prev, ...response.industries]);
        if (response.pagination) {
          setIndustriesPagination(prev => ({
            ...prev,
            skip: response.pagination!.skip,
            hasMore: response.pagination!.has_more,
            currentPage: response.pagination!.current_page
          }));
        }
      }
    } catch (error) {
      // Silent fail for pagination
    } finally {
      setIndustriesLoading(false);
    }
  };

  // Close industries modal
  const handleCloseIndustriesModal = () => {
    setIsIndustriesModalVisible(false);
    setSelectedSOCCode("");
    setSelectedJobTitle("");
    setIndustries([]);
    setIndustriesError("");
    setIndustriesPagination({
      limit: 50,
      skip: 0,
      hasMore: false,
      totalPages: 0,
      currentPage: 1,
      totalCount: 0
    });
    
    // Dispatch event to signal industries modal closed
    window.dispatchEvent(new CustomEvent('industriesModalClosed'));
  };

  // Close all industries modal
  const handleCloseAllIndustriesModal = () => {
    setIsAllIndustriesModalVisible(false);
    setSelectedSOCCode("");
    setSelectedJobTitle("");
    setAllIndustries([]);
    setAllIndustriesError("");
    setAllIndustriesLoading(false);
  };

  // Optimized event handlers with useCallback to prevent performance violations
  const handleViewIndustries = React.useCallback((event: CustomEvent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const { socCode, jobTitle } = event.detail;
    
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      handleIndustriesClick(socCode, jobTitle);
    });
  }, []);

  const handleViewBusinesses = React.useCallback((event: CustomEvent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const { socCode, jobTitle } = event.detail;
    
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      handleBusinessesClick(socCode, jobTitle);
    });
  }, []);

  const handleViewAllIndustries = React.useCallback((event: CustomEvent) => {
    const { socCode, jobTitle } = event.detail;
    requestAnimationFrame(() => {
      handleAllIndustriesClick(socCode, jobTitle);
    });
  }, []);

  // Add event listeners for JobDescription component events
  React.useEffect(() => {

    const handleShowFinancialAid = () => {
      setIsFinancialAidVisible(true);
      // Don't close the Job Detail modal - keep it underneath
    };

    const handleViewJobPostings = (event: CustomEvent) => {
      const { socCode, jobTitle } = event.detail;
      setJobPostingsSOCCode(socCode);
      setJobPostingsJobTitle(jobTitle);
      setIsJobPostingsModalVisible(true);
      // Don't close the Job Detail modal - let it stay open behind the Job Postings modal
    };

    const handleTourCloseIndustries = () => {
      handleCloseIndustriesModal();
    };

    const handleTourClickAllIndustries = () => {
      // This will be handled by the existing handleViewAllIndustries
    };

    const handleTourHeaderFinancialAid = () => {
      setIsFinancialAidVisible(true);
      // Dispatch event to signal financial aid modal opened
      window.dispatchEvent(new CustomEvent('financialAidModalOpened'));
    };

    const handleTourCloseJobDetail = () => {
      handleCloseJobDetailModal();
    };

    const handleCloseFinancialAidModal = () => {
      setIsFinancialAidVisible(false);
    };

    // Use passive listeners for better performance
    window.addEventListener('viewIndustries', handleViewIndustries as EventListener, { passive: true });
    window.addEventListener('viewAllIndustries', handleViewAllIndustries as EventListener, { passive: true });
    window.addEventListener('viewBusinesses', handleViewBusinesses as EventListener, { passive: true });
    window.addEventListener('viewJobPostings', handleViewJobPostings as EventListener, { passive: true });
    window.addEventListener('showFinancialAid', handleShowFinancialAid, { passive: true });
    window.addEventListener('tourClickCloseIndustries', handleTourCloseIndustries, { passive: true });
    window.addEventListener('tourClickAllIndustries', handleTourClickAllIndustries, { passive: true });
    window.addEventListener('tourClickHeaderFinancialAid', handleTourHeaderFinancialAid, { passive: true });
    window.addEventListener('tourCloseJobDetail', handleTourCloseJobDetail, { passive: true });
    window.addEventListener('closeFinancialAidModal', handleCloseFinancialAidModal, { passive: true });

    return () => {
      window.removeEventListener('viewIndustries', handleViewIndustries as EventListener);
      window.removeEventListener('viewAllIndustries', handleViewAllIndustries as EventListener);
      window.removeEventListener('viewBusinesses', handleViewBusinesses as EventListener);
      window.removeEventListener('viewJobPostings', handleViewJobPostings as EventListener);
      window.removeEventListener('showFinancialAid', handleShowFinancialAid);
      window.removeEventListener('tourClickCloseIndustries', handleTourCloseIndustries);
      window.removeEventListener('tourClickAllIndustries', handleTourClickAllIndustries);
      window.removeEventListener('tourClickHeaderFinancialAid', handleTourHeaderFinancialAid);
      window.removeEventListener('tourCloseJobDetail', handleTourCloseJobDetail);
      window.removeEventListener('closeFinancialAidModal', handleCloseFinancialAidModal);
    };
  }, []);

  const totalPages = Math.ceil(totalJobs / jobsPerPage);
  
  // Memoize sorted jobs to prevent expensive recalculations
  const sortedJobs = React.useMemo(() => getFilteredJobs(), [
    jobs, 
    searchTerm, 
    selectedSector, 
    selectedCareerCluster, 
    sortBy
  ]);



  return (
    <div>
      <div className="min-h-screen text-zinc-700 wall-texture-bg">

        <Header 
          onFinancialAidClick={() => setIsFinancialAidVisible((v) => !v)} 
        />
        <main className="wall-texture-bg important px-4 py-4 mx-auto my-0 max-w-[1200px]">
          <section className="mb-4">
            <SearchFilters
              searchTerm={searchTerm}
              selectedSector={selectedSector}
              selectedCareerCluster={selectedCareerCluster}
              sectors={sectors}
              careerClusters={careerClusters}
              sortBy={sortBy}
              onSearchChange={handleSearchChange}
              onSectorChange={handleSectorChange}
              onCareerClusterChange={handleCareerClusterChange}
              onSortChange={handleSortChange}
              isSearching={isSearching}
              isGrowthSortActive={isGrowthTrendSort}
              minCurrentJobsThreshold={MIN_CURRENT_JOBS_FOR_GROWTH}
              includeSmallGrowthOccupations={includeSmallGrowthOccupations}
              canAdjustMinCurrentJobs={canAdjustMinCurrentJobs}
              onIncludeSmallGrowthOccupationsChange={handleIncludeSmallGrowthOccupationsChange}
            />
            
            {/* Loading State */}
            {loading && (
              <div className="px-6 py-8 mt-4 text-center bg-white rounded-2xl border-2 border-gray-200">
                <LoadingSpinner 
                  message="Loading career opportunities..." 
                  size="lg" 
                  color="primary" 
                />
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="px-6 py-8 mt-4 text-center bg-red-50 rounded-2xl border border-red-200 border-solid">
                <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">
                  Connection Error
                </h3>
                <p className="m-0 mb-4 text-base text-red-600">
                  {error}
                </p>
                <button
                  onClick={clearError}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Results Count */}
            {!loading && !error && (
              <p 
                className="mx-0 mt-0 mb-2 text-base text-zinc-700 hover:underline cursor-pointer transition-colors"
                title="Click to view detailed filtering options and search tips"
                onClick={() => {
                  // This could open a help modal or expand search options
                }}
              >
                Showing {sortedJobs.length}
                {sortedJobs.length === 1
                  ? " career opportunity"
                  : " career opportunities"}
                {totalJobs > sortedJobs.length && (
                  <span className="opacity-70">
                    {" "}of {totalJobs} total
                  </span>
                )}
              </p>
            )}
          </section>



          {/* Jobs Grid */}
          {!loading && !error && (
            <>
              <section className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-sm:grid-cols-[1fr]">
                {sortedJobs.map((job, index) => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    onCardClick={() => handleJobCardClick(job)}
                    data-first={index === 0 ? "true" : undefined}
                  />
                ))}
              </section>
              {/* Pagination Controls */}
              {!loading && totalJobs > 0 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {(() => {
                    const pageButtons = [];
                    const startPage = Math.max(2, currentPage - 2);
                    const endPage = Math.min(totalPages - 1, currentPage + 2);
                    // Always show first page
                    pageButtons.push(
                      <button
                        key={1}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === 1
                            ? "bg-primary text-white"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setCurrentPage(1)}
                      >
                        1
                      </button>
                    );

                    // Add ellipsis if needed
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }

                    // Add middle pages
                    for (let i = startPage; i <= endPage; i++) {
                      pageButtons.push(
                        <button
                          key={i}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === i
                              ? "bg-primary text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Add ellipsis if needed
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }

                    // Always show last page if there is more than one page
                    if (totalPages > 1) {
                      pageButtons.push(
                        <button
                          key={totalPages}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === totalPages
                              ? "bg-primary text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pageButtons;
                  })()}
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {/* No Results */}
          {!loading && !error && sortedJobs.length === 0 && (
            <div className="px-6 py-8 mt-4 text-center bg-white rounded-2xl border-2 border-gray-200">
              <h3 className="mx-0 mt-0 mb-4 text-2xl font-semibold text-gray-700">
                No Career Opportunities Found
              </h3>
              <p className="m-0 mb-4 text-base text-gray-600">
                Try adjusting your search terms or filters to find more opportunities.
              </p>
            </div>
          )}
        </main>

        {FEATURE_FLAGS.SHOW_TRUSTED_ORGANIZATIONS && <TrustedOrganizations />}
        <Footer 
          onAboutClick={() => setIsAboutModalVisible(true)}
          onContactClick={() => setIsContactModalVisible(true)}
          onQuickStartClick={() => setIsQuickStartVisible(true)}
        />
      </div>

      {/* Financial Aid Modal */}
      {isFinancialAidVisible && (
        <div className="fixed inset-0 z-[10000]">
          <FinancialAid onClose={() => setIsFinancialAidVisible(false)} />
        </div>
      )}

      {/* About Modal */}
      {isAboutModalVisible && (
        <About onClose={() => setIsAboutModalVisible(false)} />
      )}

      {/* Contact Modal */}
      {isContactModalVisible && (
        <Contact onClose={() => setIsContactModalVisible(false)} />
      )}

      {isQuickStartVisible && (
        <QuickStartGuide onClose={() => setIsQuickStartVisible(false)} />
      )}

      {/* Job Postings Modal */}
      {isJobPostingsModalVisible && (
        <JobPostingsModal 
          isVisible={isJobPostingsModalVisible}
          onClose={() => setIsJobPostingsModalVisible(false)}
          socCode={jobPostingsSOCCode}
          jobTitle={jobPostingsJobTitle}
        />
      )}

      {/* Job Detail Modal */}
      {isJobDetailModalVisible && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-1">
          <div className="bg-[#ffffff] rounded-3xl shadow-2xl max-w-[95vw] w-full max-h-[98vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between p-4 border-b border-[#e6e6e6] bg-[#ffffff] gap-3">
              <div className="flex-1 min-w-0 pr-2">
                <h2 className="text-xl sm:text-2xl font-bold text-[#1b2c5a] font-['Anton'] leading-tight break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {selectedJob.title}
                </h2>
                <p className="text-[#1b2c5a] text-opacity-70 text-sm mt-1 font-['Roboto'] break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  SOC Code: {selectedJob.socCode} • {selectedJob.sector}
                </p>
              </div>
              <div className="flex items-center flex-shrink-0">
                <button
                  onClick={handleCloseJobDetailModal}
                  className="px-3 py-2 sm:px-6 sm:py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                  title="Close Modal"
                  aria-label="Close Modal"
                >
                  <svg className="w-4 h-4 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="hidden sm:inline">Close</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(98vh-120px)]">
                                  <JobDescription job={selectedJob} onetData={onetData} />
            </div>
          </div>
        </div>
      )}

            {/* Industries Modal */}
      {isIndustriesModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2">
          <div className="bg-[#ffffff] rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#e6e6e6] bg-[#ffffff]">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-[#1b2c5a] font-['Anton'] tracking-wide">
                  🏭 Industries for: {selectedJobTitle}
                </h2>
                <p className="text-[#707070] text-base mt-2 font-['Roboto']">
                  SOC Code: {selectedSOCCode} • Industry employment data and growth projections for this occupation
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {/* Download Excel Button */}
                {industries.length > 0 && (
                  <button
                    onClick={() => exportIndustriesToExcel(industries, selectedJobTitle, selectedSOCCode)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto"
                    title="Download Excel"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Download Excel</span>
                    <span className="sm:hidden">Excel</span>
                  </button>
                )}
                {/* Close Button */}
                <button
                  onClick={handleCloseIndustriesModal}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto industries-close-button"
                  title="Close Modal"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {industriesLoading ? (
                <LoadingSpinner 
                  message="Loading industries..." 
                  size="md" 
                  color="primary" 
                />
              ) : industriesError ? (
                <div className="text-center py-8">
                  <div className="text-[#d5a32a] mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold mb-2 text-[#1b2c5a] font-['Roboto_Condensed']">No Industry Data Available</h3>
                    <p className="text-sm text-[#707070] mb-4 font-['Roboto']">
                      Industry employment data is not currently available for this occupation.
                    </p>
                    <div className="bg-[#f2e8d5] p-4 rounded-lg border border-[#c1a27b]">
                      <p className="text-sm text-[#5a483b] font-['Roboto']">
                        <strong>Why this happens:</strong> Some occupations may not have detailed industry breakdown data in our current database. This doesn't mean the occupation isn't important - it just means we don't have the specific industry employment data for this SOC code.
                      </p>
                    </div>
                  </div>
                </div>
              ) : industries.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-[#a6a6a6]">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold mb-2 text-[#1b2c5a] font-['Roboto_Condensed']">No Industries Found</h3>
                    <p className="text-sm text-[#707070] font-['Roboto']">No industries are associated with this SOC code.</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Enhanced Business & Industry Summary */}
                  <div className="mb-6 p-6 bg-gradient-to-br from-[#f2e8d5] to-[#f9f1e3] rounded-xl border border-[#c1a27b] shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-[#d5a32a] rounded-full p-2">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-[#1b2c5a] font-['Roboto_Condensed'] tracking-wide">Business & Industry Overview</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                      <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-4xl font-bold text-[#d5a32a] font-['Anton'] mb-1">
                          {industries.filter(i => i.business_name).length}
                        </div>
                        <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                          🏢 Local Businesses
                        </div>
                        <div className="text-[#1b2c5a] text-xs mt-1">
                          with complete data
                        </div>
                      </div>
                      
                      <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-4xl font-bold text-[#1b2c5a] font-['Anton'] mb-1">{industries.length}</div>
                        <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                          🏭 Total Industries
                        </div>
                        <div className="text-[#1b2c5a] text-xs mt-1">
                          for this occupation
                        </div>
                      </div>
                      
                      <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-4xl font-bold text-[#f2994a] font-['Anton'] mb-1">
                          {industries.filter(i => i.employment_change > 0).length}
                        </div>
                        <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                          📈 Growing Industries
                        </div>
                        <div className="text-[#1b2c5a] text-xs mt-1">
                          increasing employment
                        </div>
                      </div>
                      
                      <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-4xl font-bold text-[#27ae60] font-['Anton'] mb-1">
                          {industries.filter(i => (i.projected_employment_2034 || i.projected_employment_2033 || 0) > (i.employment_2024 || i.employment_2023 || 0)).length}
                        </div>
                        <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                          📈 Expanding Industries
                        </div>
                        <div className="text-[#1b2c5a] text-xs mt-1">
                          projected job growth
                        </div>
                      </div>
                    </div>
                    
                    {/* Industry Stats Bar */}
                    <div className="mt-4 p-4 bg-[#ffffff] rounded-lg border border-[#c1a27b]">
                      <div className="text-center text-sm text-[#1b2c5a]">
                        <span className="font-bold">📊 Total Industries: </span>
                        <span className="text-[#d5a32a] font-bold">
                          {industries.length}
                        </span>
                        <span className="mx-4">•</span>
                        <span className="font-bold">📈 Growing: </span>
                        <span className="text-[#27ae60] font-bold">
                          {industries.filter(i => (i.employment_change || i.employment_percent_change) > 0).length}
                        </span>
                        <span className="mx-4">•</span>
                        <span className="font-bold">📉 Declining: </span>
                        <span className="text-[#e74c3c] font-bold">
                          {industries.filter(i => (i.employment_change || i.employment_percent_change) < 0).length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Industries & Businesses List */}
                  <div className="space-y-6">
                    {industries.map((industry, index) => (
                      <div key={index} className="bg-gradient-to-br from-[#ffffff] to-[#f8f9fa] rounded-2xl p-6 border-2 border-[#e6e6e6] shadow-lg hover:shadow-xl transition-all duration-300">
                        
                        {/* Industry-Only Header Design */}
                        <div className="mb-6">
                            <div className="bg-gradient-to-r from-[#1b2c5a] to-[#2d4a7c] rounded-xl p-4 mb-4">
                              <div className="flex items-center gap-3">
                                <div className="bg-white rounded-full p-2">
                                  <svg className="w-6 h-6 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-white text-sm font-bold uppercase tracking-wide">Industry</div>
                                  <div className="text-white text-2xl font-bold font-['Anton'] tracking-wide">
                                    {industry.industry_title}
                                  </div>
                                </div>
                              </div>
                            <div className="text-white/90 text-base font-medium mt-2">
                              {industry.industry_type} • Code: {industry.industry_code}
                            </div>
                          </div>
                        </div>
                        
                        {/* Growth Status */}
                        <div className="mb-5 text-center">
                          <span 
                            className={`inline-block px-4 py-2 text-sm font-bold rounded-full font-['Roboto'] cursor-help ${
                              industry.employment_change > 0 
                                ? 'bg-[#f2e8d5] text-[#5a483b] border-2 border-[#c1a27b]' 
                                : industry.employment_change < 0 
                                ? 'bg-[#e6e6e6] text-[#707070] border-2 border-[#a6a6a6]'
                                : 'bg-[#f2c94c] text-[#1b2c5a] border-2 border-[#d5a32a]'
                            }`}
                            title={industry.employment_change > 0 ? 'Employment projected to increase in this industry' : 
                                   industry.employment_change < 0 ? 'Employment projected to decrease in this industry' : 
                                   'Employment projected to remain stable in this industry'}
                          >
                            {industry.employment_change > 0 ? '📈 Growing' : 
                             industry.employment_change < 0 ? '📉 Declining' : '➡️ Stable'}
                          </span>
                        </div>
                        
                                                {/* Enhanced Business & Industry Information Grid */}
                        <div className="bg-[#f8f9fa] rounded-xl p-6 border border-[#e6e6e6]">
                          
                          {/* Business Information Section */}
                          {industry.business_name && (
                            <div className="mb-6">
                              <div className="flex items-center gap-2 mb-4">
                                <svg className="w-5 h-5 text-[#d5a32a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <h4 className="text-lg font-bold text-[#1b2c5a] font-['Roboto_Condensed']">Business Details</h4>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                
                                {/* Business Address */}
                                {industry.business_address && (
                                  <div className="col-span-full flex items-start gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">📍</span>
                                    <div className="flex-1">
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Business Address</div>
                                      <div className="text-[#1b2c5a] leading-tight font-medium mt-1">
                                        {[industry.business_address, industry.business_city, industry.business_state, industry.business_zip].filter(Boolean).join(', ')}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Business Phone */}
                                {industry.business_phone && (
                                  <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">📞</span>
                                    <div>
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Contact</div>
                                      <div className="text-[#1b2c5a] font-bold mt-1">{industry.business_phone}</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Business Employees */}
                                {industry.business_employees && (
                                  <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">👥</span>
                                    <div>
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Team Size</div>
                                      <div className="text-[#1b2c5a] font-bold mt-1">{industry.business_employees} employees</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Business Year Established */}
                                {industry.business_year_established && (
                                  <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">📅</span>
                                    <div>
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Founded</div>
                                      <div className="text-[#1b2c5a] font-bold mt-1">Est. {industry.business_year_established}</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Business Revenue */}
                                {industry.business_revenue && (
                                  <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">💰</span>
                                    <div>
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Annual Revenue</div>
                                      <div className="text-[#1b2c5a] font-bold mt-1">{industry.business_revenue}</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Business Website */}
                                {industry.business_website && (
                                  <div className="col-span-full flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors">
                                    <span className="text-[#d5a32a] text-xl">🌐</span>
                                    <div className="flex-1">
                                      <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Website</div>
                                      <a 
                                        href={formatUrl(industry.business_website)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[#d5a32a] hover:text-[#1b2c5a] underline font-bold transition-colors text-base mt-1 inline-block"
                                      >
                                        {industry.business_website}
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Industry Information Section */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <h4 className="text-lg font-bold text-[#1b2c5a] font-['Roboto_Condensed']">Industry & Employment Data</h4>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                              
                              {/* Industry Name & Code */}
                              <div className="col-span-full md:col-span-2 flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6]">
                                <span className="text-[#1b2c5a] text-xl">🏭</span>
                                <div className="flex-1">
                                  <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Industry</div>
                                  <div className="text-[#1b2c5a] font-bold mt-1">{industry.industry_title}</div>
                                  <div className="text-[#707070] text-xs font-mono mt-1">
                                    Code: {industry.industry_code ? String(industry.industry_code).replace(/,/g, '') : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Current Employment */}
                              <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6]">
                                <span className="text-[#1b2c5a] text-xl">👷</span>
                                <div>
                                  <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Current Employment (2024)</div>
                                  <div className="text-[#1b2c5a] font-bold mt-1">
                                    {(industry.employment_2024 || industry.employment_2023 || industry.employment) ? Math.round(industry.employment_2024 || industry.employment_2023 || industry.employment || 0).toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Projected Employment */}
                              <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6]">
                                <span className="text-[#1b2c5a] text-xl">📈</span>
                                <div>
                                  <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">2034 Projection</div>
                                  <div className="text-[#1b2c5a] font-bold mt-1">
                                    {(industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment) ? Math.round(industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment || 0).toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Employment Change */}
                              <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6]">
                                <span className={`text-xl ${
                                  industry.employment_change > 0 ? 'text-[#f2994a]' : 
                                  industry.employment_change < 0 ? 'text-[#707070]' : 'text-[#d5a32a]'
                                }`}>
                                  {industry.employment_change > 0 ? '📈' : 
                                   industry.employment_change < 0 ? '📉' : '➡️'}
                                </span>
                                <div>
                                  <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">Change 2024-2034</div>
                                  <div className={`font-bold mt-1 ${
                                    industry.employment_change > 0 ? 'text-[#f2994a]' : 
                                    industry.employment_change < 0 ? 'text-[#707070]' : 'text-[#d5a32a]'
                                  }`}>
                                    {industry.employment_change >= 0 ? '+' : ''}{industry.employment_change ? Math.round(industry.employment_change).toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Percent of Industry */}
                              <div className="flex items-center gap-4 p-4 bg-[#ffffff] rounded-lg border border-[#e6e6e6]">
                                <span className="text-[#1b2c5a] text-xl">📊</span>
                                <div>
                                  <div className="text-[#707070] text-xs uppercase tracking-wide font-bold">% of Industry (2024)</div>
                                  <div className="text-[#1b2c5a] font-bold mt-1">
                                    {(industry.percent_of_industry_2024 || industry.percent_of_industry_2023 || industry.percent_of_industry) ? `${(industry.percent_of_industry_2024 || industry.percent_of_industry_2023 || industry.percent_of_industry || 0).toFixed(2)}%` : 'N/A'}
                                  </div>
                                </div>
                              </div>
                              
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {industriesPagination.hasMore && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={loadMoreIndustries}
                        disabled={industriesLoading}
                        className="px-6 py-3 bg-gradient-to-r from-[#1b2c5a] to-[#2d4a7c] text-white rounded-lg hover:from-[#2d4a7c] hover:to-[#1b2c5a] transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {industriesLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            Load More Industries ({industriesPagination.totalCount - industries.length} remaining)
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Pagination Info */}
                  {industriesPagination.totalCount > 0 && (
                    <div className="mt-2 text-center text-sm text-[#707070] font-['Roboto']">
                      Showing {industries.length} of {industriesPagination.totalCount} industries
                      {industriesPagination.totalPages > 1 && (
                        <span> • Page {industriesPagination.currentPage} of {industriesPagination.totalPages}</span>
                      )}
                    </div>
                  )}

                  {/* Footer Actions */}
                  <div className="mt-6 pt-4 border-t border-[#e6e6e6]">
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={() => {
                          setIsFinancialAidVisible(true);
                          handleCloseIndustriesModal();
                        }}
                        className="px-8 py-4 bg-gradient-to-r from-[#d5a32a] to-[#f2c94c] text-[#ffffff] rounded-xl hover:from-[#f2c94c] hover:to-[#d5a32a] transition-all duration-200 font-bold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl font-['Roboto'] text-base"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        Explore Financial Aid Options
                      </button>
                      <button
                        onClick={handleCloseIndustriesModal}
                        className="px-8 py-4 bg-gradient-to-r from-[#1b2c5a] to-[#162844] text-[#ffffff] rounded-xl hover:from-[#162844] hover:to-[#1b2c5a] transition-all duration-200 font-bold shadow-lg hover:shadow-xl font-['Roboto'] text-base"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {/* Bottom Download Button */}
              {industries.length > 0 && (
                <div className="mt-6 p-4 border-t border-[#e6e6e6] bg-[#f8f9fa] rounded-b-xl">
                  <div className="flex justify-center">
                    <button
                      onClick={() => exportIndustriesToExcel(industries, selectedJobTitle, selectedSOCCode)}
                      className="px-8 py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                      title="Download Excel"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Excel Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All Industries Modal */}
      {isAllIndustriesModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#e6e6e6] bg-[#ffffff]">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-[#1b2c5a] font-['Anton'] tracking-wide">
                  All Industries for: {selectedJobTitle}
                </h2>
                <p className="text-[#707070] text-base mt-2 font-['Roboto']">
                  SOC Code: {selectedSOCCode} • Complete industry breakdown and employment projections
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {/* Download Excel Button */}
                {allIndustries.length > 0 && (
                  <button
                    onClick={() => exportIndustriesToExcel(allIndustries, selectedJobTitle, selectedSOCCode)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto"
                    title="Download Excel"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Download Excel</span>
                    <span className="sm:hidden">Excel</span>
                  </button>
                )}
                {/* Close Button */}
                <button
                  onClick={handleCloseAllIndustriesModal}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto"
                  title="Close Modal"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {allIndustriesLoading ? (
                <LoadingSpinner 
                  message="Loading all industries..." 
                  size="md" 
                  color="primary" 
                />
              ) : allIndustriesError ? (
                <div className="text-center py-8">
                  <div className="text-[#76b6f2] mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold mb-2 text-[#1b2c5a] font-['Roboto_Condensed']">No Industry Data Available</h3>
                    <p className="text-sm text-[#707070] mb-4 font-['Roboto']">
                      Industry employment data is not currently available for this occupation.
                    </p>
                    <div className="bg-[#e8f4fd] p-4 rounded-lg border border-[#76b6f2]">
                      <p className="text-sm text-[#1b2c5a] font-['Roboto']">
                        <strong>Why this happens:</strong> Some occupations may not have detailed industry breakdown data in our current database. This doesn't mean the occupation isn't important - it just means we don't have the specific industry employment data for this SOC code.
                      </p>
                    </div>
                  </div>
                </div>
              ) : allIndustries.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-[#a6a6a6]">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold mb-2 text-[#1b2c5a] font-['Roboto_Condensed']">No Industries Found</h3>
                    <p className="text-sm text-[#707070] font-['Roboto']">No industries are associated with this SOC code.</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Summary Statistics */}
                  <div className="mb-6 p-6 bg-[#e8f4fd] rounded-xl border border-[#76b6f2] shadow-sm">
                    <h3 className="text-xl font-bold text-[#1b2c5a] mb-4 font-['Roboto_Condensed'] tracking-wide">Industry Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                      <div className="text-center p-4 bg-[#ffffff] rounded-lg border border-[#76b6f2]">
                        <div className="text-3xl font-bold text-[#1b2c5a] font-['Anton']">{allIndustries.length}</div>
                        <div className="text-[#707070] font-medium font-['Roboto'] mt-1">Total Industries</div>
                      </div>
                      <div className="text-center p-4 bg-[#ffffff] rounded-lg border border-[#76b6f2]">
                        <div className="text-3xl font-bold text-[#f2994a] font-['Anton']">
                          {allIndustries.filter(i => i.employment_change > 0).length}
                        </div>
                        <div className="text-[#707070] font-medium font-['Roboto'] mt-1">Growing Industries</div>
                      </div>
                      <div className="text-center p-4 bg-[#ffffff] rounded-lg border border-[#76b6f2]">
                        <div className="text-3xl font-bold text-[#d5a32a] font-['Anton']">
                          {allIndustries.filter(i => i.employment_change === 0).length}
                        </div>
                        <div className="text-[#707070] font-medium font-['Roboto'] mt-1">Stable Industries</div>
                      </div>
                      <div className="text-center p-4 bg-[#ffffff] rounded-lg border border-[#76b6f2]">
                        <div className="text-3xl font-bold text-[#a6a6a6] font-['Anton']">
                          {allIndustries.filter(i => i.employment_change < 0).length}
                        </div>
                        <div className="text-[#707070] font-medium font-['Roboto'] mt-1">Declining Industries</div>
                      </div>
                    </div>
                  </div>

                  {/* Industries List */}
                  <div className="space-y-3">
                    {allIndustries.map((industry, index) => (
                      <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-[#1b2c5a] text-sm leading-tight flex-1 font-['Roboto']">
                            {industry.industry_title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full font-['Roboto'] ${
                            industry.employment_change > 0 
                              ? 'bg-[#f2e8d5] text-[#5a483b] border border-[#c1a27b]' 
                              : industry.employment_change < 0 
                              ? 'bg-[#e6e6e6] text-[#707070] border border-[#a6a6a6]'
                              : 'bg-[#f2c94c] text-[#1b2c5a] border border-[#d5a32a]'
                          }`}>
                            {industry.employment_change > 0 ? '📈 Growing' : 
                             industry.employment_change < 0 ? '📉 Declining' : '➡️ Stable'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div className="space-y-1">
                            <span className="font-medium text-[#707070] font-['Roboto']">Industry Code:</span>
                            <div className="text-[#1b2c5a] font-mono text-xs bg-[#e8f4fd] px-2 py-1 rounded border border-[#76b6f2]">
                              {industry.industry_code}
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <span className="font-medium text-[#707070] font-['Roboto']">Current Employment (2024):</span>
                            <div className="text-[#1b2c5a] font-semibold font-['Roboto']">
                              {(industry.employment_2024 || industry.employment_2023 || industry.employment) ? Math.round(industry.employment_2024 || industry.employment_2023 || industry.employment || 0).toLocaleString() : 'N/A'}
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <span className="font-medium text-[#707070] font-['Roboto']">Projected Employment (2034):</span>
                            <div className="text-[#1b2c5a] font-semibold font-['Roboto']">
                              {(industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment) ? Math.round(industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment || 0).toLocaleString() : 'N/A'}
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <span className="font-medium text-[#707070] font-['Roboto']">Employment Change:</span>
                            <div className={`font-semibold font-['Roboto'] ${
                              industry.employment_change > 0 ? 'text-[#f2994a]' : 
                              industry.employment_change < 0 ? 'text-[#707070]' : 'text-[#d5a32a]'
                            }`}>
                              {industry.employment_change >= 0 ? '+' : ''}{industry.employment_change ? Math.round(industry.employment_change).toLocaleString() : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {/* Bottom Download Button */}
              {allIndustries.length > 0 && (
                <div className="mt-6 p-4 border-t border-[#e6e6e6] bg-[#f8f9fa] rounded-b-xl">
                  <div className="flex justify-center">
                    <button
                      onClick={() => exportIndustriesToExcel(allIndustries, selectedJobTitle, selectedSOCCode)}
                      className="px-8 py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                      title="Download Excel"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Excel Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Businesses Modal */}
      {isBusinessesModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2">
          <div className="bg-[#ffffff] rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#e6e6e6] bg-[#ffffff]">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-[#1b2c5a] font-['Anton'] tracking-wide">
                  🏢 Businesses for: {selectedJobTitle}
                </h2>
                <p className="text-[#707070] text-base mt-2 font-['Roboto']">
                  SOC Code: {selectedSOCCode} • Local businesses and company information for this occupation
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {/* Download Excel Button */}
                {businesses.length > 0 && (
                  <button
                    onClick={() => exportBusinessesToExcel(businesses, selectedJobTitle, selectedSOCCode)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto"
                    title="Download Excel"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Download Excel</span>
                    <span className="sm:hidden">Excel</span>
                  </button>
                )}
                {/* Close Button */}
                <button
                  onClick={() => setIsBusinessesModalVisible(false)}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] w-full sm:w-auto"
                  title="Close Modal"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] bg-[#f8f9fa]">
              {businessesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner message="Loading businesses..." size="lg" color="primary" />
                </div>
              ) : businessesError ? (
                <div className="text-center py-12">
                  <div className="text-red-600 text-lg font-['Roboto']">{businessesError}</div>
                </div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-[#707070] text-lg font-['Roboto']">No businesses found for this occupation.</div>
                </div>
              ) : (
                <div>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-4xl font-bold text-[#1b2c5a] font-['Anton'] mb-1">
                        {businesses.length}
                      </div>
                      <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                        🏢 Total Businesses
                      </div>
                      <div className="text-[#1b2c5a] text-xs mt-1">
                        found for this occupation
                      </div>
                    </div>
                    
                    <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-4xl font-bold text-[#27ae60] font-['Anton'] mb-1">
                        {businesses.filter(b => b.phone).length}
                      </div>
                      <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                        📞 Contact Available
                      </div>
                      <div className="text-[#1b2c5a] text-xs mt-1">
                        with phone numbers
                      </div>
                    </div>
                    
                    <div className="text-center p-5 bg-[#ffffff] rounded-xl border-2 border-[#c1a27b] shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-4xl font-bold text-[#d5a32a] font-['Anton'] mb-1">
                        {businesses.filter(b => b.website).length}
                      </div>
                      <div className="text-[#707070] font-bold font-['Roboto'] text-xs uppercase tracking-wide">
                        🌐 Websites Available
                      </div>
                      <div className="text-[#1b2c5a] text-xs mt-1">
                        online presence
                      </div>
                    </div>
                  </div>
                  
                  {/* Business Stats Bar */}
                  <div className="mt-4 p-4 bg-[#ffffff] rounded-lg border border-[#c1a27b]">
                    <div className="text-center text-sm text-[#1b2c5a]">
                      <span className="font-bold">💼 Business Coverage: </span>
                      <span className="text-[#d5a32a] font-bold">
                        {Math.round((businesses.filter(b => b.name && b.name !== 'Unnamed business').length / businesses.length) * 100)}%
                      </span>
                      <span className="mx-4">•</span>
                      <span className="font-bold">📍 Addresses: </span>
                      <span className="text-[#d5a32a] font-bold">
                        {businesses.filter(b => b.address).length} complete
                      </span>
                      <span className="mx-4">•</span>
                      <span className="font-bold">👥 Employee Data: </span>
                      <span className="text-[#d5a32a] font-bold">
                        {businesses.filter(b => b.employees_2023).length} available
                      </span>
                    </div>
                  </div>

                  {/* Enhanced Businesses List */}
                  <div className="space-y-6 mt-6">
                  {businesses.map((business, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#ffffff] to-[#f8f9fa] rounded-2xl p-6 border-2 border-[#e6e6e6] shadow-lg hover:shadow-xl transition-all duration-300">
                      
                      {/* Business Header Design */}
                      <div className="mb-6">
                        <div className="bg-gradient-to-r from-[#d5a32a] to-[#f2c94c] rounded-xl p-4 mb-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-white rounded-full p-2">
                              <svg className="w-6 h-6 text-[#d5a32a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="text-white text-sm font-bold uppercase tracking-wide">Local Business</div>
                              <div className="text-white text-2xl font-bold font-['Anton'] tracking-wide">
                                {business.name || 'Business Opportunity'}
                              </div>
                            </div>
                          </div>
                          <div className="text-white/90 text-base font-medium mt-2">
                            {business.main_line_of_business || 'Business Operations'} • {business.industry_type || 'Industry'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Business Information Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Contact Information */}
                        <div className="bg-[#ffffff] rounded-xl p-5 border border-[#e6e6e6] shadow-sm">
                          <h4 className="text-lg font-bold text-[#1b2c5a] mb-3 font-['Anton']">📍 Contact Information</h4>
                          <div className="space-y-2">
                            {business.address && (
                              <div className="text-sm text-[#707070]">
                                <span className="font-bold">Address:</span> {[business.address, business.city, business.state, business.zip].filter(Boolean).join(', ')}
                              </div>
                            )}
                            {business.phone && (
                              <div className="text-sm text-[#707070]">
                                <span className="font-bold">Phone:</span> {business.phone}
                              </div>
                            )}
                            {business.website && (
                              <div className="text-sm">
                                <a 
                                  className="text-[#d5a32a] underline font-['Roboto'] hover:text-[#b8921e]" 
                                  href={formatUrl(business.website)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  🌐 Visit Website
                                </a>
                              </div>
                            )}
                            {business.map_url && (
                              <div className="text-sm">
                                <a 
                                  className="text-[#d5a32a] underline font-['Roboto'] hover:text-[#b8921e]" 
                                  href={formatUrl(business.map_url)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  🗺️ View on Map
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Business Details */}
                        <div className="bg-[#ffffff] rounded-xl p-5 border border-[#e6e6e6] shadow-sm">
                          <h4 className="text-lg font-bold text-[#1b2c5a] mb-3 font-['Anton']">💼 Business Details</h4>
                          <div className="space-y-2">
                            {business.employees_2023 && (
                              <div className="text-sm text-[#707070]">
                                <span className="font-bold">Employees (2023):</span> {String(business.employees_2023)}
                              </div>
                            )}

                            {business.year_established && (
                              <div className="text-sm text-[#707070]">
                                <span className="font-bold">Established:</span> {business.year_established}
                              </div>
                            )}
                            {business.public_private && (
                              <div className="text-sm text-[#707070]">
                                <span className="font-bold">Type:</span> {business.public_private}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Demo Tour */}
      <DemoTour 
        isActive={isDemoTourActive} 
        onClose={handleCloseDemoTour} 
      />

    </div>
  );
}

export default CareerPathwaysPortal; 